// 機能訓練レポート・PDF部分.
import * as React from 'react';
import * as Chart from 'chart.js';
import * as _ from 'lodash';
import * as moment from 'moment';
import { CARE_LEVELS, SECTION_PRESS_LOCOMO_NAMES, SECTION_PRESS_ADL_NAMES } from '../constants/Training';
import { User, Male, Female } from '../constants/UserListField';
import { BarthelIndexData, LineGraphData } from '../constants/BarthelIndexAPI';
import { calcAge, isValidDate } from '../utils/dateUtil';
import { MDate, zeroPadding } from '../utils/dateUtil';
// PDF系.
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as Enumerable from 'linq';

// PDF生成.
import { pdfjs } from 'react-pdf';

// Jest時にエラーにならないための処理.
if (process.env.PROCESS !== 'JEST') {
  // src/vendorの仮フォントファイル.
  const pdfFonts = require('vendor/vfs_fonts.js');

  // PDFの準備.
  const pdf = pdfMake;
  pdf.vfs = pdfFonts.pdfMake.vfs;
  pdf.fonts = {
    GenShinGothic: {
      normal: 'GenShinGothic-Normal.ttf',
      bold: 'GenShinGothic-Bold.ttf'
    }
  };

  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
}

// 折れ線グラフのサイズ.
const LINE_GRAPH_WIDTH = 200;
const LINE_GRAPH_HEIGHT = 140;

// レーダーチャートのサイズ.
const RADAR_CHART_WIDTH = 130;
const RADAR_CHART_HEIGHT = 130;

// サムネイルのサイズ.
const THUMBNAIL_WIDTH = 361;
const THUMBNAIL_HEIGHT = 294;

const DUMMY_IMAGE = 'data:image/png;base64,'
  + 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8Xw8AAoMBgDTD2qgAAAAASUVORK5CYII=';

// 主に実施したトレーニングのパネル数.
const MAX_PANEL_COUNT = 4;

// 主に実施したトレーニングにあったグレー.
const GRAY = '#C7C7C7';

// 矢印の方向を定義する型(-1はダミー, 0は同じ, 1は↑, 2は↓)
type ArrowType = -1 | 0 | 1 | 2;

/**
 * Props
 *
 * @interface PrintHeaderProps
 * @extends {React.Props<{}>}
 */
interface LiterallyReportPDFProps extends React.Props < {} > {
  user: any;
}

/**
 * PDFドキュメント.
 *
 * @export
 * @class LiterallyReportPDF
 * @extends {React.Component<LiterallyReportPDFProps, {}>}
 */
export default class LiterallyReportPDF extends React.Component < LiterallyReportPDFProps, {}> {
  // image => base64用.
  refConverter: HTMLCanvasElement | null;
  // チャート系.
  refRadarChart: HTMLCanvasElement | null;
  refCircleChart: HTMLCanvasElement | null;

  // base64文字画像.
  base64Obj: any = {};

  /**
   * 月選択の親コンポーネントからPDF生成.
   *
   * @param {string} facilityName 施設名.
   * @param {number} hdsrPoint HDS-Rの点数(ないとき-1).
   * @param {string} yearMonth YYYYMMの月日.
   * @param {*} trainingObj トレーニングのデータ.
   * @param {BarthelIndexData[]} biData BIのデータ.
   * @memberof LiterallyReportPDF
   */
  public runGeneratePDF(
    facilityName: string, hdsrPoint: number, yearMonth: string, trainingObj: any, biData: BarthelIndexData[]) {
    // 3日分(lengthが3未満もあり得る).
    const biData3Days: BarthelIndexData[] = this.pickupBIData(biData);

    // レーダーチャート用データを生成.
    const raderData: (string | number)[][] = this.convertToRaderData(biData3Days);

    // レーダーチャート作成.
    const radarChartStr = this.createRadarChart(raderData);

    // 折れ線グラフラ用データを生成.
    const lineGraphData: LineGraphData[] = this.convertToLineGraphData(biData3Days);
    const lineGraphStr = this.createLineGraph(lineGraphData);

    // PDFの生成(渡すのはADL評価の右側の表の1日分のBIデータ).
    let pdfData = {} as BarthelIndexData;

    if (biData && biData.length > 0) {
      // 配列の最後が最新.
      pdfData = biData[biData.length - 1];
    }
    this.generatePDF(facilityName, hdsrPoint, radarChartStr, lineGraphStr, yearMonth, trainingObj, pdfData);
  }

  // 10KB以下はPDFで失敗するためBase64変換.
  // https://jsfiddle.net/handtrix/YvQ5y/
  convertImgToDataURLviaCanvas = (url: any, callback: any) => {
    let img = new Image();
    let canvas = this.refConverter;
    img.onload = function () {
      if (canvas === null) {
        return;
      }
      let ctx = canvas.getContext('2d');
      canvas.width = THUMBNAIL_WIDTH;
      canvas.height = THUMBNAIL_HEIGHT;

      if (ctx === null) {
        return;
      }

      ctx.drawImage(this as any, 0, 0);
      var dataURL = canvas.toDataURL('image/png');
      callback(dataURL);
    };

    img.src = url;
  }

  componentDidMount() {
    const LOCOMO_CNT = 9;
    const THUMBNAILS_PATH = 'training_thumbnails';

    // 予め変換.
    [
      // ロコモ.
      'udeJouge',
      'udeSayu',
      'hizaNobashi',
      'momoAge',
      'mataKaihei',
      'ashiYokoage',
      'oshiri',
      'trunkCurl',
      'rowing',
      // ADL
      'ofuroMatagi',
      'ofuroSenpatsu',
      'toiletTachiagari',
      'toiletChakudatsui',
      'idoHoko',
      'sentakumonoHoshi',
    ].forEach((imgName: string, index: number) => {
      // locomo or adl
      let path = index < LOCOMO_CNT ? 'locomo' : 'adl';
      const imageUrl = this.getImage(`${THUMBNAILS_PATH}/${path}/${imgName}.png`);
      const me = this;
      this.convertImgToDataURLviaCanvas(imageUrl, (base64Img: string) => {
        me.base64Obj[imgName] = base64Img;
      });
    });
  }

  render() {
    // ブラウザの幅が小さくなっても小さくさせないため.
    const parentStyle = {
      width: '959px'
    } as React.CSSProperties;

    // 画像・チャートの生成領域を見せない.
    const canvasStyle = {
      display: 'none',
    } as React.CSSProperties;

    return (
      <div>
        {/* 画像生成のための仮置き */}
        <canvas
          ref={(item) => { this.refConverter = item; }}
          style={canvasStyle}
        />
        <div style={parentStyle}>
          <canvas
            ref={(item) => { this.refRadarChart = item; }}
            width={RADAR_CHART_WIDTH}
            height={RADAR_CHART_HEIGHT}
            style={canvasStyle}
          />
          <canvas
            ref={(item) => { this.refCircleChart = item; }}
            width={LINE_GRAPH_WIDTH}
            height={LINE_GRAPH_HEIGHT}
            style={canvasStyle}
          />
        </div>
      </div>
    );
  }

  /**
   * 年齢取得.
   *
   * @private
   * @returns
   * @memberof LiterallyReportPDF
   */
  private getAge() {
    const user: User = this.props.user;
 
    // 誕生日のデータ.
    const year = Number(user.user_birth_year);
    const month = Number(user.user_birth_month);
    const date = Number(user.user_birth_day);
    const age = calcAge(year, month, date);
    return age + '歳';
  }

  /**
   * 誕生日表示用文字列生成.
   *
   * @returns {string}
   * @memberof MoffCheck
   */
  private makeBirthday(): string {
    const user: User = this.props.user;

    const year = user.user_birth_year;
    const month = user.user_birth_month;
    const date = user.user_birth_day;

    // 数字ではないデータが入っているのを防止.
    if (!isValidDate(year, month, date)) {
      return `${year}.　.　生`;
    }

    return `${year}.${month}.${date}生`;
  }

  /**
   * カレンダーの生成.
   *
   * @private
   * @param {*} trainingObj ロコモとADLのデータ.
   * @param {string} yearMonth YYYY-MM
   * @returns {pdfMake.Content[]}
   * @memberof LiterallyReportPDF
   */
  private createCalender(trainingObj: any, yearMonth: string): pdfMake.Content[] {
    // ある日でのbooleanの配列作成.
    const boolsDateArr = this.convertToCalenderGraphData(trainingObj, yearMonth);

    // 2018-02
    let [yearSelected, monthSelected] = yearMonth.split('-');

    // 曜日のフォントサイズ.
    const weekTitleFontSize = 8;

    // まず１行目の曜日の入っただけの配列.
    const calenderContent = [
      [
        {
          style: {
            fontSize: weekTitleFontSize,
            color: '#D26940',
            alignment: 'center'
          },
          text: '日'
        }, {
          style: {
            fontSize: weekTitleFontSize,
            color: 'black',
            alignment: 'center'
          },
          text: '月'
        }, {
          style: {
            fontSize: weekTitleFontSize,
            color: 'black',
            alignment: 'center'
          },
          text: '火'
        }, {
          style: {
            fontSize: weekTitleFontSize,
            color: 'black',
            alignment: 'center'
          },
          text: '水'
        }, {
          style: {
            fontSize: weekTitleFontSize,
            color: 'black',
            alignment: 'center'
          },
          text: '木'
        }, {
          style: {
            fontSize: weekTitleFontSize,
            color: 'black',
            alignment: 'center'
          },
          text: '金'
        }, {
          style: {
            fontSize: weekTitleFontSize,
            color: '#63A3AD',
            alignment: 'center'
          },
          text: '土'
        }
      ],
    ];

    // カレンダーの塗りつぶしの赤色.
    const CALENDER_FILL_RED = '#F2775D';
    const WIDTH = 20;
    const HEIGHT = 20;

    const mDate = new MDate();
    const calender = mDate.makeCalendar(Number(yearSelected), Number(monthSelected));
    calender.map((week: {day: number|string}[]) => {
      // 1週間の7日分.
      const weekContent: any[] = [];

      week.map((day: {day: number|string}) => {
        const dateStr: string = zeroPadding(day.day);

        // 空白日はダミー文字.
        if (!day.day) {
          weekContent.push(
            {
              text: '',
              fillColor: CALENDER_FILL_RED,
              width: WIDTH,
              height: HEIGHT,
            }
          );

          return;
        }

        // 画像の取得.
        let imgName: string = '';

        // boolsDateArrは1日がindex0で入った配列.
        const realIndex = Number(day.day) - 1;

        // データが有る日.
        if (boolsDateArr[realIndex]) {
          // DONEは白丸に黒数字.
          imgName = `day_${dateStr}_done.png`;
        } else {
          // 白数字.
          imgName = `day_${dateStr}.png`;
        }

        weekContent.push(
          {
            image: this.getImage(imgName),
            fillColor: CALENDER_FILL_RED,
            width: WIDTH,
            height: HEIGHT,
            alignment: 'center'
          }
        );
      });

      // カレンダーに1週間詰める.
      calenderContent.push(weekContent);
    });

    return calenderContent;
  }

  /**
   * レーダーチャートの2日分データ生成(ない場合は詰めない). 
   * [ラベル, 今回のデータ, 前回のデータ] が入る.
   *
   * @private
   * @param {BarthelIndexData[]} biData3Days
   * @returns {((string | number)[][])}
   * @memberof LiterallyReportPDF
   */
  private convertToRaderData(biData3Days: BarthelIndexData[]): (string | number)[][] {
    // まずラベルの設定.
    const raderData = [this.antiClockwise(['食事', '移乗', '整容', 'トイレ', '入浴', '階段', '排便', '排尿', '歩行', '着替え'])];

    // 新しい順(今回のデータ -> 前回のデータ)に追加.
    const max = biData3Days.length - 1;
    const min = Math.max(0, biData3Days.length - 2);

    for (let i = max; i >= min; i--) {
      const oneDay = biData3Days[i];
      if (oneDay) {
        // '食事', '移乗', '整容', 'トイレ', '入浴', '階段', '排便', '排尿', '歩行', '着替え' の準で数字配列.
        const item = [
          // undefinedは0にならずNaNなのでそれの防止.
          Number(oneDay.feeding) || 0,
          Number(oneDay.transfers) || 0,
          Number(oneDay.grooming) || 0,
          Number(oneDay.toilet_use) || 0,
          Number(oneDay.bathing) || 0,
          Number(oneDay.stairs) || 0,
          Number(oneDay.bowels) || 0,
          Number(oneDay.bladder) || 0,
          Number(oneDay.mobility) || 0,
          Number(oneDay.dressing) || 0,
        ];

        // レーダーチャート似合わせるため順を逆転(反時計回りで単純逆転ではない)させる.
        raderData.push(this.antiClockwise(item));
      }
    }

    return raderData;
  }

  /**
   *  折れ線グラフラ用データを生成(ない場合は詰めない)
   *
   * @private
   * @param {BarthelIndexData[]} biData3Days
   * @returns {LineGraphData[]}
   * @memberof LiterallyReportPDF
   */
  private convertToLineGraphData(biData3Days: BarthelIndexData[]): LineGraphData[] {
    const lineGraphData: LineGraphData[] = [];
    for (let i = 0; i < 3; i++) {
      const oneDay = biData3Days[i];
      if (oneDay) {
        const date = oneDay.record_time;
        if (date) {
          // 各合計.
          const sum =
            // undefinedは0にならずNaNなのでそれの防止.
            (Number(oneDay.feeding) || 0) +
            (Number(oneDay.transfers) || 0) +
            (Number(oneDay.grooming) || 0) +
            (Number(oneDay.toilet_use) || 0) +
            (Number(oneDay.bathing) || 0) +
            (Number(oneDay.stairs) || 0) +
            (Number(oneDay.bowels) || 0) +
            (Number(oneDay.bladder) || 0) +
            (Number(oneDay.mobility) || 0) +
            (Number(oneDay.dressing) || 0);

          lineGraphData.push({
            'date': date,
            'count': sum,
          });
        }
      }
    }

    // ADL評価の折れ線グラフは右が最新データ.
    return lineGraphData;
  }

  /**
   * レーダーチャートは時計回りなので、一番上にあるデータは残しindex1以降のものを反転してくっつける.
   *
   * @private
   * @param {((string | number)[])} original
   * @returns {((string | number)[])}
   * @memberof LiterallyReportPDF
   */
  private antiClockwise(original: (string | number)[]): (string | number)[] {
    return original.slice(0, 1).concat(
      original.slice(1, original.length).reverse()
    );
  }

  /**
   * 棒グラフ作成.
   *
   * @private
   * @returns {string}
   * @memberof LiterallyReportPDF
   */
  private createLineGraph(biData: LineGraphData[]): string {
    if (this.refCircleChart === null) {
      return DUMMY_IMAGE;
    }

    const context = this.refCircleChart.getContext('2d');
    if (context === null) {
      return DUMMY_IMAGE;
    }

    context.canvas.width = LINE_GRAPH_WIDTH;
    context.canvas.height = LINE_GRAPH_HEIGHT;

    // 各下のラベル日付 ['YYYY年', 'MM月DD日'].
    const labels: string[][] = [];
    // 回数.
    const counts: number[] = [];
    biData.map((item) => {
      // 年と月日を分割.
      const [year, month, day] = item.date.split('-');
      labels.push([
        `${Number(year)}年`,
        `${Number(month)}年${Number(day)}日`,
      ]);

      counts.push(item.count);
    });

    const currentData = {
      label: '',
      data: counts,
      // 折れ線の下を塗りつぶさない.
      fill: false,
      // 線の色.
      borderColor: 'rgb(244,154,60)',
      // ベジェ曲線のテンション(張力)。 0を指定すると直線になります.
      lineTension: 0,
      borderWidth: 5,
      // ドットのサイズ.
      pointBorderWidth: 20,
      // ドットの色.
      pointBorderColor: 'rgb(238,88,53)',
      pointRadius: 5,
      pointBackgroundColor: 'rgb(238,88,53)',
    } as Chart.ChartDataSets;

    // データの設定
    const config = {
      type: 'line',
      data: { labels: labels, datasets: [currentData]},

      // オプション設定
      options: {
        scales: {
          xAxes: [{
            ticks: {
              fontSize: 35,
            },
            // 開始を右に少しずらす.
            offset: true
          }],
          yAxes: [{
            ticks: {
              fontSize: 35,
              beginAtZero: true,
            }
          }]
        },
        // アニメーションをオフにしないとtoBase64Image()時に画像生成できない.
        animation: {
          duration: 0,
        },
        // □の説明を消す.
        legend: {
          display: false,
        }
      }
    } as Chart.ChartConfiguration;

    const myLineChart = new Chart(context, config);
    const str = ((myLineChart as any).toBase64Image());
    return str;
  }

  /**
   * 3日分取得する.
   * ない場合はlengthが3未満のこともある.
   *
   * @private
   * @param {BarthelIndexData[]} biData
   * @returns {BarthelIndexData[]}
   * @memberof LiterallyReportPDF
   */
  private pickupBIData(biData: BarthelIndexData[]): BarthelIndexData[] {
    if (biData === null) {
      return [];
    }

    const arr = Enumerable.from(biData)
      // 存在確認.
      .where(element => { return element.created_at !== ''; })
      // 時間の新しい順から(record_timeは年月日しか無いので).
      .orderByDescending(element => element.created_at)
      // 3日分.
      .take(3)
      // ただし逆順
      .reverse()
      .toArray();

    return arr;
  }

  /**
   * レーダーチャート作成.
   *
   * @private
   * @returns
   * @memberof LiterallyReportPDF
   */
  private createRadarChart(biData: (string | number)[][]): string {
    if (this.refRadarChart === null) {
      return DUMMY_IMAGE;
    }

    const context = this.refRadarChart.getContext('2d');
    if (context === null) {
      return DUMMY_IMAGE;
    }

    context.canvas.width = RADAR_CHART_WIDTH;
    context.canvas.height = RADAR_CHART_HEIGHT;

    // 最新データ.
    const currentData = {
        label: '最新',
        data: biData[1],
        // 線(ピンク).
        borderColor: '#E95F85',
        // 線の内側を塗りつぶす色(塗りつぶさないため透明).
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderWidth: 10,
        // ドット.
        pointBackgroundColor: '#9C124D',
        pointRadius: 7,
    };

    // 前回.
    const beforeData = {
        // 最新との表示の間にスペースを入れるため.
        label: '前回　',
        data: biData[2],
        // 線.
        borderColor: '#F5AD5E',
        // 線の内側を塗りつぶす色(塗りつぶさないため透明).
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderWidth: 10,
        // ドット.
        pointBackgroundColor: '#D17700',
        pointRadius: 7,
    };
    // ラベル(横軸)
    const label = biData[0];

    // データの設定
    const config = {
      // グラフの種類（レーダーチャートを指定）.
      type: 'radar',
      data: {
        labels: label,
        datasets: [beforeData, currentData],
      },
      // オプション設定
      options: {
        // アニメーションをオフにしないとtoBase64Image()時に画像生成できない.
        animation: {
          duration: 0,
          // 三角関数とfillTextを利用して各目盛りに15, 10, 5, 0を記載する.
          onComplete: function() {
            // 型がないのでanyを使う.
            const chartInstance = (this as any).chart,
                ctx = chartInstance.ctx;

            ctx.font = Chart.helpers.fontString(35, 'bold', Chart.defaults.global.defaultFontFamily);
            ctx.fillStyle = '#000000';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';

            /*
            これらの「o」を一旦表示させて当たりをつける.
            // 中心の座標決めの実験
            ctx.fillText('o', 500, 520);
            // max決めの実験.
            ctx.fillText('o', 500, 140);
            */
            // 半径を求めるために、Yの最大値(文字とかぶらせないため若干下にする).
            const maxY = 140;

            // 十角形.
            const deltaAngle = 360 / 10;
            // 中心の座標(x:左から, y:上から).
            const baseX = 500;
            const baseY = 520;
            // 半径.
            let r = baseY - maxY;
           
            // 歩行から右回りで最大値を格納した配列.
            const maxValues = [
               15, 10, 10, 10, 5, 10, 5, 15, 10, 10,
            ];

            // ラベル名.
            [15, 10, 5, 0].map((myLabel: number, n: number) => {
              // maxからだんだん短くする.
              const currentR = r - r / 4 * n;

              let index = 0;
              for (let currentAngle = -(deltaAngle / 2); currentAngle < 360; currentAngle += deltaAngle) {
                let degree = currentAngle;
                let radian = degree * (Math.PI / 180);

                // 最大値以下のものだけ表示.
                if (myLabel <= maxValues[index]) {
                  ctx.fillText(myLabel, currentR * Math.cos(radian) + baseX, currentR * Math.sin(radian) + baseY);
                }

                index++;
              }
            });
          }
        },
        legend: {
          // □の説明の場所.
          position: 'top',
          labels: { 
            fontSize: 35,
          }
        },
        // レスポンシブ指定.
        // responsive: true,
        // スケールの設定.
        scale: {
          pointLabels: {
            // 各項目名のフォントサイズ.
            fontSize: 40,
            fontStyle: 'bold',
            fontColor: 'black',
          },
          // 目盛り.
          ticks: {
            display: false,
            autoSkip: false,
            fontSize: 35,
            // fontStyle: 'bold',
            fontColor: 'black',
            // 目盛り値のカスタマイズ.
            stepSize: 5,
            // 最小値の値を0指定.
            beginAtZero: true,
            min: -5,
            // 最大値を指定.
            max: 15,
          },
        }
      }
    } as Chart.ChartConfiguration;

    const myChartGraph = new Chart(context, config);
    const str = ((myChartGraph as any).toBase64Image());
    return str;
  }

  /**
   * 画像の取得.
   *
   * @private
   * @param {string} imagePath
   * @returns {string}
   * @memberof LiterallyReportPDF
   */
  private getImage(imagePath: string): string {
    const image: string = require(`../img/${imagePath}`);
    return image;
  }

  /**
   * 主に実施したトレーニングのパネル生成.
   *
   * @private
   * @memberof LiterallyReportPDF
   */
  private createTrainingPanel = (
    trainingNo: number, currentCount: number, beforeCount: number): pdfMake.Content => {
    const ORANGE = '#F08C02';
    const BROWN = '#8B5729';

    const isLocomo = trainingNo < 100;
    let fillColor = isLocomo ? ORANGE : BROWN;
    let title = '';
    let imgName = '';

    switch (trainingNo) {
      // ロコモ系.
      case 1:
        title = SECTION_PRESS_LOCOMO_NAMES[0];
        imgName = 'udeJouge';
        break;
      case 2:
        title = SECTION_PRESS_LOCOMO_NAMES[1];
        imgName = 'udeSayu';
        break;
      case 3:
        title = SECTION_PRESS_LOCOMO_NAMES[2];
        imgName = 'hizaNobashi';
        break;
      case 4:
        title = SECTION_PRESS_LOCOMO_NAMES[3];
        imgName = 'momoAge';
        break;
      case 5:
        title = SECTION_PRESS_LOCOMO_NAMES[4];
        imgName = 'mataKaihei';
        break;
      case 6:
        title = SECTION_PRESS_LOCOMO_NAMES[5];
        imgName = 'ashiYokoage';
        break;
      case 7:
        title = SECTION_PRESS_LOCOMO_NAMES[6];
        imgName = 'oshiri';
        break;
      case 8:
        title = SECTION_PRESS_LOCOMO_NAMES[7];
        imgName = 'trunkCurl';
        break;
      case 9:
        title = SECTION_PRESS_LOCOMO_NAMES[8];
        imgName = 'rowing';
        break;
      // 日常生活動作(ADL)系,
      case 101:
        title = SECTION_PRESS_ADL_NAMES[0];
        imgName = 'ofuroMatagi';
        break;
      case 102:
        title = SECTION_PRESS_ADL_NAMES[1];
        imgName = 'ofuroSenpatsu';
        break;
      case 103:
        title = SECTION_PRESS_ADL_NAMES[2];
        imgName = 'toiletTachiagari';
        break;
      case 104:
        title = SECTION_PRESS_ADL_NAMES[3];
        imgName = 'toiletChakudatsui';
        break;
      case 105:
      case 106:
        // 欠番.
        break;
      case 107:
        title = SECTION_PRESS_ADL_NAMES[6];
        imgName = 'idoHoko';
        break;
      case 108:
        // TODO: 履歴、モフトレ通信ではまだなのでそちらは後で直す.
        // title = SECTION_PRESS_ADL_NAMES[7];
        title = '洗濯物干し';
        imgName = 'sentakumonoHoshi';
        break;
      default:
        break;
    }

    // 今回矢印種類.
    let currentArrowType: ArrowType = 0;
    if (currentCount > beforeCount) {
      currentArrowType = 1;
    } else if (currentCount < beforeCount) {
      currentArrowType = 2;
    }

    return [
      {
        // タイトル部分な塗りつぶしのため要素1つのテーブル.
        style: 'table',
        table: {
          widths: ['*'],
          body: [
            [
              {
                text: title,
                style: 'trainingHeader',
                alignment: 'left',
              }
            ]
          ]
        },
        margin: [0, 2, 0, 3],
        fillColor: fillColor,
        layout: 'noBorders',
      }, {
        // 画像の枠のため要素1つのテーブル.
        style: 'table',
        table: {
          // widths: ['*'],
          borderWidth: 0.1,
          body: [
            [
              {
                // サムネイル.
                image: this.base64Obj[imgName],
                // 左寄せにしないと収まってくれない.
                alignment: 'left',
                // 大きさ調整(縦は自動).
                width: 132,
                margin: [-3, -1, -3, -1],
              }
            ]
          ],
        },
        layout: {
          vLineColor: (i: number, node: any) => {
            return GRAY;
          },
          hLineColor: (i: number, node: any) => {
            return GRAY;
          },
        }
      },
      // 今回回数.
      this.createTrainingCountPanel(currentCount, true, currentArrowType),
      // 前回回数.
      this.createTrainingCountPanel(beforeCount, false, -1),
    ];
  }

  /**
   * トレーニングID と 回数 に変換.
   *
   * @private
   * @memberof LiterallyReportPDF
   */
  private convertTrainingData = (currentLocomo: any, currentAdl: any, prevLocomo: any, prevAdl: any): any => {
    const currentLocomoCnt: any = {};
    for (const date of Object.keys(currentLocomo)) {
      const values = currentLocomo[date];
      for (const key of Object.keys(values)) {
        // keyはtraining idになっている.
        if (currentLocomoCnt[key]) {
          currentLocomoCnt[key] += values[key].count || 0;
        } else {
          currentLocomoCnt[key] = values[key].count || 0;
        }
      }
    }

    const currentAdlCnt: any = {};
    for (const date of Object.keys(currentAdl)) {
      const values = currentAdl[date];
      for (const key of Object.keys(values)) {
        if (currentAdlCnt[key]) {
          currentAdlCnt[key] += values[key].count || 0;
        } else {
          currentAdlCnt[key] = values[key].count || 0;
        }
      }
    }

    const prevLocomoCnt: any = {};
    for (const date of Object.keys(prevLocomo)) {
      const values = prevLocomo[date];
      for (const key of Object.keys(values)) {
        if (prevLocomoCnt[key]) {
          prevLocomoCnt[key] += values[key].count || 0;
        } else {
          prevLocomoCnt[key] = values[key].count || 0;
        }
      }
    }

    const prevAdlCnt: any = {};
    for (const date of Object.keys(prevAdl)) {
      const values = prevAdl[date];
      for (const key of Object.keys(values)) {
        if (prevAdlCnt[key]) {
          prevAdlCnt[key] += values[key].count || 0;
        } else {
          prevAdlCnt[key] = values[key].count || 0;
        }
      }
    }

    return {
      // 今月.
      current: {
        locomo: currentLocomoCnt,
        adl: currentAdlCnt,
      },
      // 先月.
      prev: {
        locomo: prevLocomoCnt,
        adl: prevAdlCnt,
      },
    };
  }

  /**
   * 不用なものを排除し、PDF表示用に [[トレーニングID, 今回, 前回]] に変換.
   *
   * @private
   * @memberof LiterallyReportPDF
   */
  private convertForPDF = (countData: any): number[][] => {
    let res: number[][] = [];

    const locomoKeys = Object.keys(countData.current.locomo);
    const adlKeys = Object.keys(countData.current.adl);

    let locomoCnt = 0;
    let adlCnt = 0;
    // ロコモ2以上、ADL2以上パターン.
    if (locomoKeys.length >= 2) {
      if (Object.keys(countData.current.adl).length >= 2) {
        // ロコモ2以上、ADL2以上パターン.
        // ロコモとADLを2ずつ詰める.
        locomoCnt = 2;
        adlCnt = 2;
      } else {
        // ロコモ2以上、ADL2未満パターン.
        // 4 - ADLの数分(ただし、ロコモの数をこえないように)、ロコモを詰めて、ADLを全部詰める.
        adlCnt = adlKeys.length;
        locomoCnt = Math.min(MAX_PANEL_COUNT - adlCnt, locomoKeys.length);
      }
    } else {
      if (adlKeys.length >= 2) {
        // ロコモ2未満、ADL2以上パターン.
        // ロコモを全部詰めて、4 - ロコモの数分(ただし、ADLの数をこえないように)、ADLを詰める.
        locomoCnt = locomoKeys.length;
        adlCnt = Math.min(MAX_PANEL_COUNT - locomoCnt, adlKeys.length);
      } else {
        // ロコモ2未満、ADL2未満パターン.
        // ロコモ、ADLを全部詰める.
        locomoCnt = locomoKeys.length;
        adlCnt = adlKeys.length;
      }
    }

    // ロコモ.
    for (let i = 0; i < locomoCnt; i++) {
      const trainingId = locomoKeys[i];
      // prevはcurrentのトレーニングIDを使うので値があるとは限らないので||0.
      res.push([
        Number(trainingId),
        countData.current.locomo[trainingId], countData.prev.locomo[trainingId] || 0
      ]);
    }

    // ADL
    for (let j = 0; j < adlCnt; j++) {
      const trainingId = adlKeys[j];
      // prevはcurrentのトレーニングIDを使うので値があるとは限らないので||0.
      res.push([
        Number(trainingId),
        countData.current.adl[trainingId], countData.prev.adl[trainingId] || 0
      ]);
    }
    return res;
  }

  private convertToCalenderGraphData = (trainingObj: any, yearMonth: string): boolean[] => {
    // 今月で日数を見る.
    let days = moment(yearMonth).daysInMonth();
    const res: boolean[] = [];

    for (let i = 0; i < days; i++) {
      // YYYY-MM-DD
      const day = `${yearMonth}-${('00' + (i + 1)).slice(-2)}`;
      let oneDayOK = false;
      if (trainingObj.locomo && trainingObj.locomo[day]) {
        const values =  trainingObj.locomo[day];
        for (const key of Object.keys(values)) {
          if (values[key].count > 0) {
            // 1つでも0以上ならそこで終了.
            oneDayOK = true;
            break;
          }
        }
      }

      // ロコモでOKならもうADL無視.
      if (oneDayOK) {
        res.push(true);
        continue;
      }

      if (trainingObj.adl && trainingObj.adl[day]) {
        const values =  trainingObj.adl[day];
        for (const key of Object.keys(values)) {
          if (values[key].count > 0) {
            // 1つでも0以上ならそこで終了.
            oneDayOK = true;
            break;
          }
        }
      }

      res.push(oneDayOK);
    }

    return res;
  }

  /**
   * 先月と今月のデータのみピックアップし、[今月ロコモ, 今月ADL, 先月ロコモ, 先月ADL]の配列を返却.
   *
   * @private
   * @memberof LiterallyReportPDF
   */
  private pickupPrevCurrentData = (trainingObj: any, yearMonth: string): any[] => {
    // 引数trainingObjには13ヶ月分のトレーニングデータがあるので、今月と先月の2ヶ月分使うための選定.
    const [year, month] = yearMonth.split('-');
    let currentLocomo: any = {};
    let currentAdl: any = {};
    let prevLocomo: any = {};
    let prevAdl: any = {};

    // 先月.
    let prevMonth = moment(`${year}-${month}-01`).add(-1, 'months').format('YYYY-MM');

    // ロコモ.
    for (const locomoYearMonth of Object.keys(trainingObj.locomo)) {
      // 今月.
      if (_.includes(locomoYearMonth, yearMonth)) {
        currentLocomo[locomoYearMonth] = trainingObj.locomo[locomoYearMonth];
      }

      // 先月.
      if (_.includes(locomoYearMonth, prevMonth)) {
        prevLocomo[locomoYearMonth] = trainingObj.locomo[locomoYearMonth];
      }
    }

    // ADL.
    for (const adlYearMonth of Object.keys(trainingObj.adl)) {
      // 今月.
      if (_.includes(adlYearMonth, yearMonth)) {
        currentAdl[adlYearMonth] = trainingObj.adl[adlYearMonth];
      }
      
      // 先月.
      if (_.includes(adlYearMonth, prevMonth)) {
        prevAdl[adlYearMonth] = trainingObj.adl[adlYearMonth];
      }
    }

    // [今月ロコモ, 今月ADL, 先月ロコモ, 先月ADL]の配列を返却.
    return [currentLocomo, currentAdl, prevLocomo, prevAdl];
  }

  /**
   * 主に実施したトレーニングのテーブル生成.
   *
   * @private
   * @memberof LiterallyReportPDF
   */
  private createTrainingTable = (trainingObj: any, yearMonth: string): any => {

    let panels: pdfMake.Content = [];

    // 2ヶ月分をピックアップ.
    const [currentLocomo, currentAdl, prevLocomo, prevAdl] = this.pickupPrevCurrentData(trainingObj, yearMonth);

    // 表示用のトレーニング選択処理.
    let countData = this.convertTrainingData(currentLocomo, currentAdl, prevLocomo, prevAdl);

    // 不用なものを排除し、PDF表示用に変換.
    const forPdfData = this.convertForPDF(countData);
    forPdfData.map((item) => {
      // トレーニングID、今回回数、前回回数.
      if (item.length === 3) {
        panels.push(this.createTrainingPanel(item[0], item[1], item[2]));
      }
    });

    return {
      style: 'table',
      table:  {
        widths: new Array(panels.length).fill(135),
        body: [
          panels,
        ]
      },
      layout: 'noBorders',
    };
  }

  /**
   * 主に実施したトレーニングの回数＋矢印部分のパネル生成.
   *
   * @private
   * @memberof LiterallyReportPDF
   */
  private createTrainingCountPanel = (count: number, isCurrent: boolean, arrowType: ArrowType): any => {
    let type = '前回';

    // グリーン.
    let fillColor = '#B5E67F';

    // 前回か今回か.
    if (isCurrent) {
      type = '今回';
      // ベージュ.
      fillColor = '#FFEBD9';
    }

    // 矢印の種類.
    let arrow = 'icon_keep';
    if (arrowType === 1) {
      // 上向き.
      arrow = 'icon_up';
    } else {
      // 下向き.
      arrow = 'icon_down';
    }

    const imgSrc: string = this.getImage(`${arrow}.png`);
    const arrowItem = {
      // 矢印.
      image: imgSrc,
      width: 15,
      alignment: 'center',
    };

    return {
      style: 'table',
      table: {
        widths: [45, '*', 45],
        body: [
          [
            {
              style: 'trainingCountText',
              text: type,
              alignment: 'right',
            },
            // -1はダミー.
            arrowType === -1 ? {} : arrowItem,
            {
              style: 'trainingCountText',
              text: `${count}回`,
              alignment: 'left',
            }
          ]
        ]
      },
      margin: [0, 3, 0, 3],
      fillColor: fillColor,
      layout: 'noBorders',
    };
  }

  /**
   * ADL評価の右側の表の右側の1行分生成.
   *
   * @private
   * @memberof LiterallyReportPDF
   */
  private createADLLine = (type: string, biData: BarthelIndexData): pdfMake.Content[] => {
    // その項目の点数.
    const point: string = biData[type];

    interface CheckData {
      // 項目名.
      name: string;
      // 選択される点数.
      point: string;
      // ※かどうか.
      special?: boolean;
    }

    let adlCheckData: {[key: string]: CheckData[]; } = {
      // 食事.
      'feeding': [{ name: '自立', point: '10' }, { name: '部分介助', point: '5' }, { name: '全介助', point: '0' }, ],
      // 車椅子からベッドへの移動.
      'transfers': [{ name: '自立', point: '15' }, { name: '部分介助', point: '10'}, { name: 'ほぼ全介助', point: '5' },
        { name: '全介助', point: '0' }, ],
      // 整容. 
      'grooming': [{ name: '自立', point: '5'}, { name: '介助', point: '0' }, ],
      // トイレ動作.
      'toilet_use': [{ name: '自立', point: '10'}, { name: '部分介助', point: '5' }, { name: '全介助', point: '0' }, ], 
      // 入浴.
      'bathing': [{ name: '自立', point: '5'}, { name: '部分介助', point: '0' }, ],
      // 階段昇降.
      'stairs': [{ name: '自立', point: '10'}, { name: '介助・監視', point: '5' }, { name: '全介助', point: '0' }, ],
      // 排便コントロール.
      'bowels': [{ name: '失禁なし', point: '10'}, { name: 'ときに失禁・介助', point: '5' }, { name: '左記以外', point: '0' }, ],
      // 排尿コントロール.
      'bladder': [{ name: '失禁なし', point: '10'}, { name: 'ときに失禁・介助', point: '5' }, { name: '左記以外', point: '0' }, ],
      // 歩行.
      'mobility':  [{ name: '歩行', point: '15' }, { name: '介助歩行', point: '10'}, { name: '車椅子', point: '5' },
        { name: '左記以外', point: '0' }, ],
      // 着替え.
      'dressing': [{ name: '自立', point: '10'}, { name: '部分介助', point: '5', special: true },
        { name: '全介助', point: '0' }, ], 
    };

    const ret: pdfMake.Content[] = [];
    adlCheckData[type].map((item: CheckData) => {
      // 今回のに該当.
      const isHit = item.point === point;

      ret.push(
        {
          // 今回のに該当ならピンク色.
          color: isHit ? '#E95F85' : 'black',
          // ※の有無.
          text: item.special ? `　${item.name}:${item.point}点※` : `　${item.name}:${item.point}点`,
          fontSize: 8,
          // 若干下げる.
          margin: [0, 4, 0, 0],
          // 該当なら太字.
          bold: isHit,
        },
      );
    });

    return ret;
  }

  /**
   * ADLの大きいテキスト＋小さいテキストの括弧書きを生成する.
   *
   * @private
   * @memberof LiterallyReportPDF
   */
  private createADLTableTitle = (large: string, small: string): any => {
    return [
      {
        text: large,
        fontSize: 10,
      },
      {
        text: `(${small})`,
        fontSize: 8,
      },
    ];
  }

  /**
   * PDF全体の生成.
   *
   * @private
   * @memberof LiterallyReportPDF
   */
  private generatePDF = (facilityName: string, hdsrPoint: number, radarChartBase64: string,
                         lineGraphBase64: string, yearMonth: string, trainingObj: any, biData: BarthelIndexData) => {
    const defaultStyle = 'GenShinGothic';
    const CALENDER_RED = '#D1664C';

    // 施設名.
    // 年月取得.
    const [year, month] = yearMonth.split('-');

    // 右上用の表示.
    const titleText = `${facilityName}\n${year}年　${month}月`;

    // HDSRの点数(データがないときは-).
    let pointText = '-';
    if (hdsrPoint >= 0) {
      pointText = `HDS-R ${hdsrPoint}点`;
    }

    // ドキュメント用のタイトル.
    const docText = `${this.props.user.user_name}_${year}${month}`;

    const docDefinition: pdfMake.TDocumentDefinitions = {
      info: {
        title: docText,
        // これらは不用.
        // author: 'bar',
        // subject: 'fuga',
        // keywords: 'moff',
      },
      // ページの余白(左, 上, 右, 下).
      pageMargins: [13, 9, 3, 3],
      pageSize: 'A4' as pdfMake.PageSize,
      // 縦.
      pageOrientation: 'PORTRAIT' as pdfMake.PageOrientation,
      content: [
        {
          style: 'table',
          table: {
            widths: [370, 190],
            body: [
              [
                {
                  // ロゴ.
                  image: this.getImage('logo.png'),
                  margin: [10, 0, 0, 0],
                  width: 160,
                },
                {
                  style: {
                    fontSize: 17,
                    bold: true,
                    alignment: 'right',
                  },
                  text: titleText,
                },
              ],
            ],
          },
          // 横並びにするためだけのダミー表なので線はない.
          layout: 'noBorders',
        },
        {
          style: 'subHeader',
          text: '利用者'
        },
        // 利用者情報.
        {
          style: 'table',
          table: {
            widths: [90, 70, 100, 70, 90, 90],
            // 高さ22で埋める6つ要素の配列.
            heights: new Array(6).fill(22),
            body: [
              [
                {
                  // 利用者名.
                  style: {
                    fontSize: 15,
                    color: 'white',
                    alignment: 'center'
                  },
                  text: this.props.user.user_name,
                  // 利用者名だけ塗りつぶし.
                  fillColor: '#F08C02',
                }, {
                  // 性別.
                  style: {
                    fontSize: 15,
                    alignment: 'center'
                  },
                  text: (this.props.user.user_gender === 0
                    ? Male
                    : Female) + '性'
                }, {
                  // 生年月日.
                  style: {
                    fontSize: 15,
                    alignment: 'center'
                  },
                  text: this.makeBirthday()
                }, {
                  // 年齢.
                  style: {
                    fontSize: 15,
                    alignment: 'center'
                  },
                  text: this.getAge()
                }, {
                  // 要介護度.
                  style: {
                    fontSize: 15,
                    alignment: 'center'
                  },
                  text: CARE_LEVELS[this.props.user.user_care_level]
                }, {
                  // 点数.
                  style: {
                    fontSize: 15,
                    alignment: 'center'
                  },
                  text: pointText,
                }
              ]
            ]
          },
          layout: {
            // 太線.
            hLineWidth: (i: number, node: any) => {
              return 2;
            },
            vLineWidth: (i: number, node: any) => {
              return 2;
            },
            // オレンジ色.
            hLineColor: (i: number, node: any) => {
              return '#F08C02';
            },
            vLineColor: (i: number, node: any) => {
              return '#F08C02';
            }
          }
        },
        // トレーニング日 & ADL評価 の横並び.
        {
          style: 'table',
          table: {
            widths: [290, 260],
            heights: ['*', 200],
            body: [
              [
                {
                  style: 'subHeader',
                  text: 'トレーニング日'
                },
                {
                  style: 'subHeader',
                  text: 'ADL評価(バーセルインデックス)の推移'
                }
              ],
              [// カレンダー.
                {
                  style: 'table',
                  table: {
                    body: this.createCalender(trainingObj, yearMonth),
                    widths: new Array(7).fill(32),
                  },
                  layout: {
                    // 赤い線の色.
                    hLineColor: (i: number, node: any) => {
                      return CALENDER_RED;
                    },
                    vLineColor: (i: number, node: any) => {
                      return CALENDER_RED;
                    },
                  }
                },
                // 折れ線グラフ.
                {
                  image: lineGraphBase64,
                  width: 256,
                }
              ]
            ]
          },
          // 横並びにするためだけのダミー表なので線はない.
          layout: 'noBorders'
        },
        // ADL評価のレーダーチャート & 表の横並び.
        {
          style: 'table',
          table: {
            body: [
              [
                {
                  style: 'subHeader',
                  text: 'ADL評価(バーセルインデックス)'
                },
                // 右側ダミータイトル.
                ''
              ],
              [
                // レーダーチャート.
                {
                  image: radarChartBase64,
                  width: 205,
                  margin: [0, 0, 0, 0],
                },
                {
                  style: 'table',
                  table: {
                    headerRows: 1,
                    body: [
                      [
                        {
                          text: '食事',
                          style: 'adlTableTitle'
                        },
                        {
                          text: this.createADLLine('feeding', biData),
                          // 左のマージン.
                          margin: [10, 0, 0, 0],
                        },
                      ],
                      [
                        {
                          // 移動(車椅子からベット)
                          text: this.createADLTableTitle('移動', '車椅子からベット'),
                          style: 'adlTableTitle'
                        },
                        {
                          text: this.createADLLine('transfers', biData),
                          // 左のマージン.
                          margin: [10, 0, 0, 0],
                        },
                      ],
                      [
                        {
                          text: '整容',
                          style: 'adlTableTitle'
                        },
                        {
                          text: this.createADLLine('grooming', biData),
                          // 左のマージン.
                          margin: [10, 0, 0, 0],
                        },
                      ],
                      [
                        {
                          text: 'トイレ動作',
                          style: 'adlTableTitle'
                        },
                        {
                          text: this.createADLLine('toilet_use', biData),
                          // 左のマージン.
                          margin: [10, 0, 0, 0],
                        },
                      ],
                      [
                        {
                          text: '入浴',
                          style: 'adlTableTitle'
                        },
                        {
                          text: this.createADLLine('bathing', biData),
                          // 左のマージン.
                          margin: [10, 0, 0, 0],
                        },
                      ],
                      [
                        {
                          text: '階段昇降',
                          style: 'adlTableTitle'
                        },
                        {
                          text: this.createADLLine('stairs', biData),
                          // 左のマージン.
                          margin: [10, 0, 0, 0],
                        },
                      ],
                      [
                        {
                          text: '排便',
                          style: 'adlTableTitle'
                        },
                        {
                          text: this.createADLLine('bowels', biData),
                          // 左のマージン.
                          margin: [10, 0, 0, 0],
                        },
                      ],
                      [
                        {
                          text: '排尿',
                          style: 'adlTableTitle'
                        },
                        {
                          text: this.createADLLine('bladder', biData),
                          // 左のマージン.
                          margin: [10, 0, 0, 0],
                        },
                      ],
                      [
                        {
                          text: this.createADLTableTitle('歩行', '45m以上の'),
                          style: 'adlTableTitle'
                        },
                        {
                          text: this.createADLLine('mobility', biData),
                          // 左のマージン.
                          margin: [10, 0, 0, 0],
                        },
                      ],
                      [
                        {
                          text: '着替え',
                          style: 'adlTableTitle'
                        },
                        {
                          text: this.createADLLine('dressing', biData),
                          // 左のマージン.
                          margin: [10, 0, 0, 0],
                        },
                      ],
                    ],
                    margin: [0, 0, 0, 0],
                  },
                  layout: {
                    // 横線.
                    hLineWidth: (i: number, node: any) => {
                      return 1.5;
                    },
                    hLineColor: (i: number, node: any) => {
                      // 最初と最後以外は黄色っぽいオレンジ.
                      return (i === 0 || i === 10) ? 'white' : '#F49F00';
                    },
                    vLineWidth: (i: number, node: any) => {
                      // 縦線を消す.
                      return 0;
                    },
                  },
                  // 右の表の上にマージンを置き若干下げる.
                  margin: [0, 10, 0, 0],
                },
              ],
              [
                // 左側ダミータイトル.
                '',
                {
                  text: '※標準的な時間内、半分以上は自分で行うことができる場合も5点となる',
                  style: {
                    fontSize: 6,
                    bold: true,
                    alignment: 'left',
                  },
                  margin: [10, 0, 0, 0],
                },
              ],
            ],
          },
          // 横並びにするためだけのダミー表なので線はない.
          layout: 'noBorders'
        },
        {
          style: 'subHeader',
          text: '主に実施したトレーニング'
        },
        // 主に実施したトレーニング.
        this.createTrainingTable(trainingObj, yearMonth),
      ],
      defaultStyle: {
        font: defaultStyle
      },
      styles: {
        // styleで文字列で指定できる.
        header: {
          fontSize: 30
        },
        // 各項目のオレンジ色の見出し.
        subHeader: {
          fontSize: 14,
          color: '#F08C02',
          // 少し上と下を開ける.
          margin: [0, 5, 0, 2],
          bold: true,
        },
        // ADL評価(バーセルインデックス)の右表示の項目タイトル.
        adlTableTitle: {
          fontSize: 10,
          bold: true,
          alignment: 'center',
        },
        // 主に実施したトレーニングのヘッダ.
        trainingHeader: {
          fontSize: 12,
          color: 'white',
          margin: [8, 4, 2, 2],
          bold: true,
        },
        // 主に実施したトレーニングの回数.
        trainingCountText: {
          fontSize: 12,
          bold: true,
        }
      }
    };

    // PDF生成.
    const pdfDocGenerator = pdfMake.createPdf(docDefinition);

    // ダウンロード時に生成される名前.
    const pdfName = `${docText}.pdf`;
    pdfDocGenerator.download(pdfName);
  }
}



// WEBPACK FOOTER //
// ./src/components/LiterallyReportPDF.tsx